import { useState } from 'react';
import { useT } from '../../i18n/i18n';
import { shareReportFiles } from '../../report/shareAll';
import { addCommentAction, commentsExportText, removeCommentAction, sortedComments, updateCommentAction, useComments, type SavedComment } from '../../state/commentLibrary';
import { Button, Card, ConfirmSheet, InlineMessage, SectionHeader, TextArea, showToast } from '../../ui/components';

// Settings › 我的评语 (Owner decision list 2026-10-19 item 33): add, edit, delete and export the inspector's
// own reusable wording. Inserting happens from every text field (「评语库」 under it).
export function CommentsPage() {
  const { t, tp } = useT();
  const comments = useComments();
  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<SavedComment | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  function add() {
    const outcome = addCommentAction(draft);
    if (outcome === 'empty') {
      setDraftError(t('comments.required'));
      return;
    }
    setDraftError(null);
    if (outcome === 'added') setDraft('');
    showToast(outcome === 'added' ? t('assist.saved') : outcome === 'exists' ? t('assist.exists') : t('assist.full'));
  }

  async function exportAll() {
    setMessage(null);
    const text = commentsExportText(comments);
    const outcome = await shareReportFiles([{ fileName: `${t('comments.exportFile')}.txt`, blob: new Blob([text], { type: 'text/plain;charset=utf-8' }) }]);
    if (outcome.code === 'failed') setMessage({ ok: false, text: t('comments.exportFailed', { detail: outcome.detail }) });
    else if (outcome.code !== 'cancelled') setMessage({ ok: true, text: tp('comments.exported', comments.length) });
  }

  return (
    <>
      <Card testId="comments-add-card">
        <p className="card-note">{t('comments.intro')}</p>
        <TextArea id="comments-new" value={draft} onChange={(v) => { setDraft(v); setDraftError(null); }} placeholder={t('comments.addPlaceholder')} rows={3} testId="comments-new" />
        {draftError ? (
          <p className="field-error" role="alert" data-testid="comments-new-error">
            {draftError}
          </p>
        ) : null}
        <div className="card-actions">
          <Button icon="add" onClick={add} testId="comments-add">
            {t('comments.add')}
          </Button>
        </div>
      </Card>
      <Card testId="comments-list">
        <SectionHeader
          title={t('comments.title')}
          meta={comments.length > 0 ? t('comments.count', { n: comments.length }) : t('comments.empty')}
          action={
            comments.length > 0 ? (
              <Button variant="text" size="small" icon="download" onClick={() => void exportAll()} testId="comments-export">
                {t('comments.export')}
              </Button>
            ) : undefined
          }
        />
        {message ? <InlineMessage kind={message.ok ? 'ok' : 'err'} text={message.text} /> : null}
        {sortedComments(comments).map((comment) =>
          editing?.id === comment.id ? (
            <div className="comment-row" key={comment.id} data-testid="comment-row">
              <TextArea value={editing.text} onChange={(text) => setEditing({ id: comment.id, text })} rows={3} testId="comment-edit-input" />
              <div className="card-actions">
                <Button
                  onClick={() => {
                    if (updateCommentAction(comment.id, editing.text)) setEditing(null);
                  }}
                  testId="comment-edit-save"
                >
                  {t('comments.save')}
                </Button>
                <Button variant="text" onClick={() => setEditing(null)} testId="comment-edit-cancel">
                  {t('shell.cancel')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="comment-row" key={comment.id} data-testid="comment-row">
              <p className="comment-text">{comment.text}</p>
              <p className="card-note">{tp('comments.used', comment.usedCount)}</p>
              <div className="card-actions">
                <Button variant="secondary" size="small" icon="edit" onClick={() => setEditing({ id: comment.id, text: comment.text })} testId="comment-edit">
                  {t('comments.edit')}
                </Button>
                <Button variant="text" size="small" icon="delete" onClick={() => setPendingDelete(comment)} testId="comment-delete">
                  {t('comments.delete')}
                </Button>
              </div>
            </div>
          )
        )}
      </Card>
      {pendingDelete ? (
        <ConfirmSheet
          title={t('comments.deleteTitle')}
          body={t('comments.deleteBody')}
          confirmLabel={t('comments.delete')}
          danger
          onConfirm={() => {
            removeCommentAction(pendingDelete.id);
            setPendingDelete(null);
          }}
          onCancel={() => setPendingDelete(null)}
        />
      ) : null}
    </>
  );
}
